import React from "react";
import { FiChevronDown, FiInfo } from "react-icons/fi";

type MultipleAccountsHelpGuideProps = {
  variant?: "page" | "compact";
  defaultExpanded?: boolean;
  className?: string;
};

const CHROME_PROFILES_HELP_URL =
  "https://support.google.com/chrome/answer/2364824";

export default function MultipleAccountsHelpGuide({
  variant = "page",
  defaultExpanded = false,
  className = "",
}: MultipleAccountsHelpGuideProps) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const isCompact = variant === "compact";

  const title = isCompact
    ? "Multiple accounts?"
    : "How to connect multiple LinkedIn accounts";

  const steps = [
    {
      title: "Use one Chrome profile per LinkedIn account",
      body: "Automate With Extension uses the LinkedIn account already logged into this Chrome profile. If you connect different senders from the same profile, they can overwrite each other.",
    },
    {
      title: "Set up each sender in its own profile",
      body: 'In Chrome, open the profile menu → Add → create a profile (e.g. "LinkedIn – Sales 1"). Log into the correct LinkedIn account in that profile only.',
    },
    {
      title: "Connect from Innlead in that profile",
      body: "Open the Innlead dashboard in the same Chrome profile, go to Accounts → Connect Account → Automate With Extension, and complete the flow while logged into the matching LinkedIn account.",
    },
    {
      title: "Repeat for each additional seat",
      body: "Switch to the next Chrome profile, log into the next LinkedIn account, and connect the next sender. You can also use Automate on Cloud for some accounts and the extension for others.",
    },
  ];

  return (
    <div
      className={`rounded-[8px] border border-brand-blue/30 bg-bg-light-blue text-left ${className}`}
      data-bypass-plan-gate="true"
    >
      <button
        type="button"
        className="flex w-full items-start gap-3 px-4 py-3 text-left"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <span
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-brand-blue"
          aria-hidden
        >
          <FiInfo className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-dark-black">{title}</span>
          {!expanded ? (
            <span className="mt-0.5 block text-xs text-dark-grey">
              {isCompact
                ? "One Chrome profile per LinkedIn account — tap to read the guide."
                : "Required when using multiple seats — each sender needs its own Chrome profile."}
            </span>
          ) : null}
        </span>
        <FiChevronDown
          className={`mt-1 h-5 w-5 shrink-0 text-brand-blue transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>

      {expanded ? (
        <div
          className={`border-t border-brand-blue/20 px-4 pb-4 pt-1 ${
            isCompact ? "space-y-3" : "space-y-4"
          }`}
        >
          <p className="text-xs leading-relaxed text-dark-grey">
            Connecting more than one sender with the extension requires a separate{" "}
            <span className="font-medium text-dark-black">Chrome profile</span> for each
            LinkedIn account so they do not conflict.
          </p>

          <ol className={`list-none ${isCompact ? "space-y-2.5" : "space-y-3"}`}>
            {steps.map((step, index) => (
              <li key={step.title} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-brand-blue">
                  {index + 1}
                </span>
                <span className="min-w-0">
                  <span className="block text-xs font-semibold text-dark-black">
                    {step.title}
                  </span>
                  <span className="mt-0.5 block text-xs leading-relaxed text-dark-grey">
                    {step.body}
                  </span>
                </span>
              </li>
            ))}
          </ol>

          <div className="rounded-[8px] border border-proxy-warning-border bg-proxy-warning-bg px-3 py-2.5">
            <p className="text-xs leading-relaxed text-proxy-warning-text">
              <span className="font-semibold">Important:</span> If you connect a second
              LinkedIn account from the same Chrome profile without switching profiles, the
              previous sender may disconnect.
            </p>
          </div>

          <p className="text-xs text-dark-grey">
            Need help creating Chrome profiles?{" "}
            <a
              href={CHROME_PROFILES_HELP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-brand-blue underline underline-offset-2 hover:opacity-90"
            >
              View Google&apos;s guide
            </a>
          </p>
        </div>
      ) : null}
    </div>
  );
}
